import type { CSSProperties, ReactNode } from 'react';
import {
  siAirtable, siAsana, siDiscord, siGooglesheets, siHubspot, siJira, siMake, siNotion, siTrello, siZapier,
  siZendesk,
} from 'simple-icons';

/**
 * The applications' own marks for the Deliver-to destinations (R6 follow-up).
 *
 * The rail and the cards show each destination as its
 * brand: a tile in the brand's colour with its mark in white, the way the
 * design draws them. Most marks come from `simple-icons` (CC0 paths, with
 * the brand's hex). Slack, Microsoft Teams, Salesforce, monday.com and
 * Freshdesk were removed from that set under their owners' brand guidelines,
 * so those five are drawn here by hand as simple, recognisable marks on the
 * brand colour. Email and Webhook are the product's own built-ins and get
 * ink tiles with a plain glyph.
 *
 * Anything the table does not know keeps the catalogue's emoji, so a new
 * integration shows up before anybody draws it a mark.
 */

interface Brand {
  title: string;
  /** the tile's colour */
  hex: string;
  /** a 24×24 path filled with the mark's colour */
  path?: string;
  /** a hand-drawn mark, when there is no single path */
  draw?: ReactNode;
  /** the mark's colour on the tile; white unless said otherwise */
  fg?: string;
}

const own = (title: string, hex: string, draw: ReactNode, fg?: string): Brand => ({ title, hex, draw, fg });

const BRANDS: Record<string, Brand> = {
  zapier: { title: siZapier.title, hex: siZapier.hex, path: siZapier.path },
  make: { title: siMake.title, hex: siMake.hex, path: siMake.path },
  zendesk: { title: siZendesk.title, hex: siZendesk.hex, path: siZendesk.path },
  discord: { title: siDiscord.title, hex: siDiscord.hex, path: siDiscord.path },
  hubspot: { title: siHubspot.title, hex: siHubspot.hex, path: siHubspot.path },
  jira: { title: siJira.title, hex: siJira.hex, path: siJira.path },
  trello: { title: siTrello.title, hex: siTrello.hex, path: siTrello.path },
  asana: { title: siAsana.title, hex: siAsana.hex, path: siAsana.path },
  airtable: { title: siAirtable.title, hex: siAirtable.hex, path: siAirtable.path },
  notion: { title: siNotion.title, hex: siNotion.hex, path: siNotion.path },
  googlesheets: { title: siGooglesheets.title, hex: siGooglesheets.hex, path: siGooglesheets.path },

  // The Slack mark: four lollipops, two per axis, in one colour.
  slack: own('Slack', '4A154B', (
    <g fill="currentColor">
      <path d="M5.04 15.17a2.52 2.52 0 1 1-2.52-2.52h2.52zM6.31 15.17a2.52 2.52 0 0 1 5.04 0v6.31a2.52 2.52 0 0 1-5.04 0z" />
      <path d="M8.83 5.04a2.52 2.52 0 1 1 2.52-2.52v2.52zM8.83 6.31a2.52 2.52 0 0 1 0 5.04H2.52a2.52 2.52 0 0 1 0-5.04z" />
      <path d="M18.96 8.83a2.52 2.52 0 1 1 2.52 2.52h-2.52zM17.69 8.83a2.52 2.52 0 0 1-5.04 0V2.52a2.52 2.52 0 0 1 5.04 0z" />
      <path d="M15.17 18.96a2.52 2.52 0 1 1-2.52 2.52v-2.52zM15.17 17.69a2.52 2.52 0 0 1 0-5.04h6.31a2.52 2.52 0 0 1 0 5.04z" />
    </g>
  )),
  // Teams: the T on its tile.
  teams: own('Microsoft Teams', '6264A7', (
    <text x="12" y="17.5" textAnchor="middle" fontSize="15" fontWeight="700" fontFamily="Segoe UI, Arial, sans-serif" fill="currentColor">T</text>
  )),
  // Salesforce: the cloud.
  salesforce: own('Salesforce', '00A1E0', (
    <path fill="currentColor" d="M9.6 5.5a4.2 4.2 0 0 1 3.4 1.8 3.4 3.4 0 0 1 2.2-.8 3.5 3.5 0 0 1 3.3 2.4A3.6 3.6 0 0 1 22 12.5a3.6 3.6 0 0 1-3.6 3.6h-.3a3.2 3.2 0 0 1-4.8 1.1 3.6 3.6 0 0 1-3.8.4 4 4 0 0 1-3.2 1.6A4.1 4.1 0 0 1 2.2 15.1a4.1 4.1 0 0 1 2.3-3.7A3.6 3.6 0 0 1 8 7.5c.4 0 .8.1 1.2.2A4.2 4.2 0 0 1 9.6 5.5z" />
  )),
  // monday.com: three slanted strokes in its three colours, on white.
  monday: own('monday.com', 'FFFFFF', (
    <g strokeWidth="3.6" strokeLinecap="round" fill="none">
      <path d="M3.5 15.5 8.5 7.5" stroke="#FF3D57" />
      <path d="M9.5 16.5 14.5 8.5" stroke="#FFCB00" />
      <circle cx="18.6" cy="15.6" r="2.4" fill="#00CA72" stroke="none" />
    </g>
  ), '#18191C'),
  // Freshdesk: the speech bubble.
  freshdesk: own('Freshdesk', '25C16F', (
    <path fill="currentColor" d="M12 3.5a8.5 8.5 0 0 1 8.5 8.5v5.6a2.9 2.9 0 0 1-2.9 2.9H12a8.5 8.5 0 0 1 0-17zm0 4.2a4.3 4.3 0 1 0 0 8.6h4.3v-4.3A4.3 4.3 0 0 0 12 7.7z" />
  )),
  // The built-ins: an envelope, and the braces a webhook posts.
  email: own('Email', '3F434B', (
    <path fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" d="M3.5 6.5h17v11h-17zM3.5 7l8.5 6.5L20.5 7" />
  )),
  webhook: own('Webhook', '3F434B', (
    <text x="12" y="16.5" textAnchor="middle" fontSize="13" fontWeight="700" fontFamily="Geist Mono, Menlo, monospace" fill="currentColor">{'{}'}</text>
  )),
};

/** The brand a connection type is shown as, or null for one the table does not know. */
export function brandOf(type?: string | null): (Brand & { type: string }) | null {
  if (!type) return null;
  const b = BRANDS[type];
  return b ? { ...b, type } : null;
}

/** The mark alone, in `currentColor`. */
export function BrandIcon({ type, size = 16, className }: { type: string; size?: number; className?: string }) {
  const b = brandOf(type);
  if (!b) return null;
  return (
    <svg
      className={className} width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" focusable="false"
      style={{ display: 'block', flex: 'none' }}
    >
      {b.path ? <path d={b.path} fill="currentColor" /> : b.draw}
    </svg>
  );
}

/**
 * A tile: the brand's colour with its mark, or the fallback glyph on the
 * plain tile everything else gets. `className` is the tile's own class
 * (`wf-tile`, the rail's `glyph`), so each place keeps its size.
 */
export function BrandTile({ type, fallback, className, size = 15, style }: {
  type?: string | null;
  fallback: ReactNode;
  className: string;
  size?: number;
  style?: CSSProperties;
}) {
  const b = brandOf(type);
  if (!b) return <span className={className} style={style}>{fallback}</span>;
  return (
    <span
      className={`${className} brand`} data-brand={b.type} title={b.title}
      style={{ background: `#${b.hex}`, color: b.fg || '#fff', borderColor: b.hex === 'FFFFFF' ? undefined : 'transparent', ...style }}
    >
      <BrandIcon type={b.type} size={size} />
    </span>
  );
}
